import { Truck, X, CheckCircle2 } from 'lucide-react';
import type { SamsaraVehicle } from '@/types';

interface VehicleConfirmModalProps {
  vehicle: SamsaraVehicle;
  onConfirm: () => void;
  onClose: () => void;
  confirmLabel?: string;  // defaults to "Confirm"
  hideCancel?: boolean;   // hides the Cancel button
}

export function VehicleConfirmModal({ 
  vehicle, onConfirm, onClose, 
  confirmLabel = 'Confirm', hideCancel = false 
}: VehicleConfirmModalProps) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-sm rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <h2 className="font-bold text-wire3-navy">Vehicle Found</h2>
          <button onClick={onClose} className="rounded-full p-1.5 text-gray-400 hover:bg-gray-100">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Vehicle details */}
        <div className="px-5 py-4">
          <div className="flex items-center gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-wire3-navy/5">
              <Truck className="h-7 w-7 text-wire3-navy" />
            </div>
            <div>
              <p className="text-lg font-bold text-wire3-navy">{vehicle.name}</p>
              <p className="text-sm text-gray-500">
                {vehicle.year} {vehicle.make} {vehicle.model}
              </p>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3">
            {[
              { label: 'VIN', value: vehicle.vin },
              { label: 'License Plate', value: vehicle.licensePlate ?? '—' },
              { label: 'Odometer', value: vehicle.odometerMeters
                ? `${Math.round(vehicle.odometerMeters / 1609.34).toLocaleString()} mi`
                : '—'
              },
              { label: 'Unit ID', value: vehicle.id },
            ].map(({ label, value }) => (
              <div key={label} className="rounded-lg bg-gray-50 px-3 py-2">
                <p className="text-xs text-gray-400">{label}</p>
                <p className="mt-0.5 text-sm font-semibold text-gray-800 truncate">{value}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 border-t border-gray-100 px-5 py-4">
          {!hideCancel && (
            <button onClick={onClose} className="btn-secondary flex-1">
              Cancel
            </button>
          )}
          <button onClick={onConfirm} className={`btn-primary ${hideCancel ? 'w-full' : 'flex-1'}`}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}